/* import __COLOCATED_TEMPLATE__ from './platform-switcher.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';
import type Workspace from 'embercom/objects/workspace';
import type Store from '@ember-data/store';
import type RouterService from '@ember/routing/router-service';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

interface Args {
  item: {
    componentAttrs: {};
  };
}

interface Signature {
  Args: Args;
}

interface PlatformOption {
  name: string;
  value: string;
  icon: InterfaceIconName;
}

export default class PlatformSwitcher extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare session: any;
  @service declare store: Store;
  @service declare appService: any;

  @service declare router: RouterService;

  get workspace(): Workspace {
    return this.session.workspace;
  }

  get app() {
    return this.appService.app;
  }

  get platforms(): PlatformOption[] {
    return [
      {
        name: this.intl.t('standalone.navbar.platform-switcher.zendesk'),
        value: 'zendesk',
        icon: 'zendesk',
      },
      {
        name: this.intl.t('standalone.navbar.platform-switcher.salesforce'),
        value: 'salesforce',
        icon: 'salesforce',
      },
      {
        name: this.intl.t('standalone.navbar.platform-switcher.freshdesk'),
        value: 'freshdesk',
        icon: 'freshdesk',
      },
      {
        name: this.intl.t('standalone.navbar.platform-switcher.hubspot'),
        value: 'hubspot',
        icon: 'hubspot',
      },
      {
        name: this.intl.t('standalone.navbar.platform-switcher.unknown'),
        value: 'unknown',
        icon: 'circle-filled',
      },
    ];
  }

  get currentPlatform(): string {
    let firstPlatform = this.workspace.standalonePlatforms?.[0];
    return firstPlatform?.platform_type || 'unknown';
  }

  get currentPlatformLabel(): string {
    let platform = this.platforms.find((p) => p.value === this.currentPlatform);
    return platform?.name || this.intl.t('standalone.navbar.platform-switcher.unknown');
  }

  // This function is a Popper.js modifier (https://popper.js.org/docs/v2/modifiers/)
  // that keeps the dimensions of the platform switcher popover the same as those of
  // the primary nav menu popover. This behavior matches the theme-switcher pattern.
  matchPopoverDimensionsToMenuPopover(popperModifierData: any) {
    let { reference: referenceElement, popper: popperElement } = popperModifierData.state.elements;
    let referenceElementPopoverContainer = referenceElement.closest('[data-popover-content]');
    popperElement.style.width = `${referenceElementPopoverContainer.offsetWidth}px`;
  }

  @action selectPlatform(platformType: string) {
    // Update workspace standalonePlatforms array
    this.workspace.standalonePlatforms = [{ platform_type: platformType }];

    // Update app standalone_platforms attribute
    if (this.app) {
      this.app.set('standalone_platforms', [{ platform_type: platformType }]);
    }

    if (!this.app.hasStandalonePlatform(platformType)) {
      throw new Error(`Failed to switch to platform ${platformType}`);
    }

    this.router.transitionTo('apps.app.standalone-platform-switch');
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Navbar::PlatformSwitcher': typeof PlatformSwitcher;
    'standalone/navbar/platform-switcher': typeof PlatformSwitcher;
  }
}
